// Geometrische hulpfuncties: snap-to-vertex, polygoon hit-test, centroid.
// Coördinaten zijn waar niet anders aangegeven [lng, lat] in WGS84.

import type { Block } from "../types/scenario";

export type LngLat = [number, number];

export interface ScreenPoint {
  x: number;
  y: number;
}

/**
 * Vind het dichtstbijzijnde vertex van een willekeurig blok binnen `pixelRadius`
 * van het schermpunt. Gebruikt voor snap-to-vertex bij tekenen.
 *
 * `project` zet een [lng,lat] om naar schermcoördinaten — wordt door MapLibre
 * geleverd via `map.project`.
 */
export function findSnapVertex(
  blocks: Block[],
  cursor: ScreenPoint,
  project: (lngLat: LngLat) => ScreenPoint,
  pixelRadius: number,
  excludeBlockId?: string,
): LngLat | null {
  let best: { lngLat: LngLat; distance: number } | null = null;
  for (const block of blocks) {
    if (block.id === excludeBlockId) continue;
    for (const vertex of block.polygon) {
      const screen = project(vertex);
      const dx = screen.x - cursor.x;
      const dy = screen.y - cursor.y;
      const d = Math.hypot(dx, dy);
      if (d <= pixelRadius && (best === null || d < best.distance)) {
        best = { lngLat: vertex, distance: d };
      }
    }
  }
  return best?.lngLat ?? null;
}

/**
 * Vind het dichtstbijzijnde vertex *binnen* een specifiek blok — gebruikt
 * tijdens vertex-verslepen om te bepalen welk hoekpunt opgepakt wordt.
 * Geeft de index van het vertex terug, of -1.
 */
export function findVertexIndex(
  block: Block,
  cursor: ScreenPoint,
  project: (lngLat: LngLat) => ScreenPoint,
  pixelRadius: number,
): number {
  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = 0; i < block.polygon.length; i++) {
    const screen = project(block.polygon[i]);
    const d = Math.hypot(screen.x - cursor.x, screen.y - cursor.y);
    if (d <= pixelRadius && d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Klassieke ray-casting om te checken of een [lng,lat] binnen een polygoon
 * ligt. Werkt voor onze schaal (kleine polygonen rond Hilversum) prima zonder
 * spherical-correctie.
 */
export function pointInPolygon(point: LngLat, polygon: LngLat[]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Centroid (zwaartepunt van vertices) — gebruikt voor "centreren in beeld". */
export function polygonCentroid(polygon: LngLat[]): LngLat {
  let lngSum = 0;
  let latSum = 0;
  for (const [lng, lat] of polygon) {
    lngSum += lng;
    latSum += lat;
  }
  return [lngSum / polygon.length, latSum / polygon.length];
}

/**
 * Bounding box [west, south, east, north] van een polygoon-collectie.
 * Geeft `null` terug bij lege input.
 */
export function blocksBounds(blocks: Block[]): [number, number, number, number] | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  let any = false;
  for (const b of blocks) {
    for (const [lng, lat] of b.polygon) {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
      any = true;
    }
  }
  return any ? [west, south, east, north] : null;
}
